import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  CheckSquare, Phone, Mail, Users, StickyNote, Clock,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { useCalendarEvents, type CalendarEvent } from "@/hooks/use-calendar"

const DAY_NAMES = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"]
const MONTH_NAMES = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
]

const PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "var(--primary)",
  high: "#F59E0B",
  urgent: "#EF4444",
}

const ACTIVITY_ICONS: Record<string, typeof Phone> = {
  phone: Phone,
  email: Mail,
  meeting: Users,
  note: StickyNote,
}

type ViewMode = "month" | "week" | "agenda"

function getMonthDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)

  // Monday-based week (0=Mon, 6=Sun)
  let startDayOfWeek = firstDay.getDay() - 1
  if (startDayOfWeek < 0) startDayOfWeek = 6

  const days: Array<{ date: Date; isCurrentMonth: boolean }> = []

  // Days from previous month
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const d = new Date(year, month, -i)
    days.push({ date: d, isCurrentMonth: false })
  }

  // Days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true })
  }

  // Fill remaining to complete 6 rows
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false })
  }

  return days
}

function getWeekDays(date: Date) {
  const day = date.getDay()
  const monday = new Date(date)
  monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1))

  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    days.push(d)
  }
  return days
}

function formatDateKey(date: Date): string {
  return date.toISOString().split("T")[0]
}

function EventDot({ event }: { event: CalendarEvent }) {
  const navigate = useNavigate()
  const isTask = event.type === "task"
  const color = isTask ? (PRIORITY_COLORS[event.priority || "normal"]) : "#14B8A6"
  const Icon = isTask ? CheckSquare : (ACTIVITY_ICONS[event.activity_type || "note"] || Clock)
  const isCompleted = event.status === "completed"

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        navigate(event.entity_url)
      }}
      className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-left transition-colors hover:bg-muted/50 group"
      title={`${event.title}${event.time ? ` · ${event.time}` : ""}`}
    >
      <Icon
        size={10}
        className="shrink-0"
        style={{ color, opacity: isCompleted ? 0.5 : 1 }}
      />
      <span
        className={`text-[10px] leading-tight truncate text-muted-foreground ${isCompleted ? "line-through" : ""}`}
      >
        {event.time && <span className="font-medium" style={{ color }}>{event.time} </span>}
        {event.title}
      </span>
    </button>
  )
}

export function CalendarPage() {
  const navigate = useNavigate()
  const today = new Date()
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [view, setView] = useState<ViewMode>("month")

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    if (view === "month") {
      const days = getMonthDays(year, month)
      return {
        start: formatDateKey(days[0].date),
        end: formatDateKey(days[days.length - 1].date),
      }
    } else if (view === "week") {
      const weekDays = getWeekDays(currentDate)
      return {
        start: formatDateKey(weekDays[0]),
        end: formatDateKey(weekDays[6]),
      }
    } else {
      // agenda: current month
      return {
        start: `${year}-${String(month + 1).padStart(2, "0")}-01`,
        end: formatDateKey(new Date(year, month + 1, 0)),
      }
    }
  }, [year, month, view, currentDate])

  const { data, isLoading } = useCalendarEvents({
    start_date: dateRange.start,
    end_date: dateRange.end,
  })

  const events = data?.data ?? []

  // Group events by date
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {}
    events.forEach((event) => {
      const key = event.date
      if (!map[key]) map[key] = []
      map[key].push(event)
    })
    return map
  }, [events])

  const navigatePrev = () => {
    if (view === "month") {
      setCurrentDate(new Date(year, month - 1, 1))
    } else if (view === "week") {
      const d = new Date(currentDate)
      d.setDate(d.getDate() - 7)
      setCurrentDate(d)
    } else {
      setCurrentDate(new Date(year, month - 1, 1))
    }
  }

  const navigateNext = () => {
    if (view === "month") {
      setCurrentDate(new Date(year, month + 1, 1))
    } else if (view === "week") {
      const d = new Date(currentDate)
      d.setDate(d.getDate() + 7)
      setCurrentDate(d)
    } else {
      setCurrentDate(new Date(year, month + 1, 1))
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), view === "week" ? today.getDate() : 1))
  }

  const todayKey = formatDateKey(today)
  const monthDays = view === "month" ? getMonthDays(year, month) : []
  const weekDays = view === "week" ? getWeekDays(currentDate) : []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarIcon size={20} className="text-primary" />
          <h1 className="page-title">
            Kalendarz
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday}>
            Dziś
          </Button>
          <div className="flex items-center">
            <Button variant="ghost" size="sm" onClick={navigatePrev}>
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-medium min-w-[160px] text-center text-foreground">
              {view === "week"
                ? `${weekDays[0]?.getDate()}-${weekDays[6]?.getDate()} ${MONTH_NAMES[weekDays[0]?.getMonth() ?? month]} ${weekDays[0]?.getFullYear() ?? year}`
                : `${MONTH_NAMES[month]} ${year}`
              }
            </span>
            <Button variant="ghost" size="sm" onClick={navigateNext}>
              <ChevronRight size={16} />
            </Button>
          </div>
          <div className="flex rounded-md border">
            {(["month", "week", "agenda"] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                {v === "month" ? "Miesiąc" : v === "week" ? "Tydzień" : "Agenda"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {/* Month View */}
      {view === "month" && !isLoading && (
        <div className="surface-card overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b">
            {DAY_NAMES.map((name) => (
              <div
                key={name}
                className="px-2 py-2 text-center text-xs font-medium text-muted-foreground"
              >
                {name}
              </div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7">
            {monthDays.map(({ date, isCurrentMonth }, i) => {
              const key = formatDateKey(date)
              const dayEvents = eventsByDate[key] || []
              const isToday = key === todayKey
              const maxVisible = 3

              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-b border-r p-1 ${isToday ? "bg-blue-500/[0.04]" : ""} ${isCurrentMonth ? "" : "opacity-40"}`}
                >
                  <div className="flex items-center justify-between px-1">
                    <span
                      className={`text-xs font-medium ${isToday ? "flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    >
                      {date.getDate()}
                    </span>
                  </div>
                  <div className="mt-0.5 space-y-px">
                    {dayEvents.slice(0, maxVisible).map((event) => (
                      <EventDot key={`${event.type}-${event.id}`} event={event} />
                    ))}
                    {dayEvents.length > maxVisible && (
                      <span className="px-1 text-[10px] text-muted-foreground">
                        +{dayEvents.length - maxVisible} więcej
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === "week" && !isLoading && (
        <div className="surface-card overflow-hidden">
          <div className="grid grid-cols-7">
            {weekDays.map((date, i) => {
              const key = formatDateKey(date)
              const dayEvents = eventsByDate[key] || []
              const isToday = key === todayKey

              return (
                <div
                  key={i}
                  className="border-r last:border-r-0"
                >
                  <div
                    className={`border-b px-2 py-2 text-center ${isToday ? "bg-blue-500/[0.06]" : ""}`}
                  >
                    <div className="text-xs text-muted-foreground">
                      {DAY_NAMES[i]}
                    </div>
                    <div
                      className={`text-lg font-semibold mt-0.5 ${isToday ? "flex h-8 w-8 mx-auto items-center justify-center rounded-full bg-primary text-primary-foreground" : "text-foreground"}`}
                    >
                      {date.getDate()}
                    </div>
                  </div>
                  <div className="min-h-[300px] p-1 space-y-1">
                    {dayEvents.map((event) => (
                      <button
                        key={`${event.type}-${event.id}`}
                        onClick={() => navigate(event.entity_url)}
                        className="w-full rounded-md border px-2 py-1.5 text-left transition-colors hover:bg-muted/30"
                      >
                        <div className="flex items-center gap-1.5">
                          {event.type === "task" ? (
                            <CheckSquare size={12} style={{ color: PRIORITY_COLORS[event.priority || "normal"] }} />
                          ) : (
                            (() => {
                              const Icon = ACTIVITY_ICONS[event.activity_type || "note"] || Clock
                              return <Icon size={12} className="text-teal-500" />
                            })()
                          )}
                          <span className="text-xs font-medium truncate text-foreground">
                            {event.title}
                          </span>
                        </div>
                        {event.time && (
                          <p className="text-[10px] mt-0.5 pl-[18px] text-muted-foreground">
                            {event.time}{event.end_time ? ` - ${event.end_time}` : ""}
                          </p>
                        )}
                      </button>
                    ))}
                    {dayEvents.length === 0 && (
                      <div className="flex items-center justify-center h-full">
                        <span className="text-[10px] text-muted-foreground">-</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Agenda View */}
      {view === "agenda" && !isLoading && (
        <div className="surface-card overflow-hidden">
          {events.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              Brak wydarzeń w tym okresie
            </p>
          ) : (
            <div className="divide-y divide-border">
              {(() => {
                let currentDateStr = ""
                return events.map((event) => {
                  const showDateHeader = event.date !== currentDateStr
                  currentDateStr = event.date

                  const eventDate = new Date(event.date + "T00:00:00")
                  const dayName = DAY_NAMES[(eventDate.getDay() + 6) % 7]
                  const isToday = event.date === todayKey

                  return (
                    <div key={`${event.type}-${event.id}`}>
                      {showDateHeader && (
                        <div
                          className={`px-4 py-2 text-xs font-semibold uppercase bg-muted ${isToday ? "text-primary" : "text-muted-foreground"}`}
                        >
                          {dayName}, {eventDate.getDate()} {MONTH_NAMES[eventDate.getMonth()]}
                          {isToday && " — Dziś"}
                        </div>
                      )}
                      <button
                        onClick={() => navigate(event.entity_url)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30"
                      >
                        <div className="w-14 text-right shrink-0">
                          {event.time ? (
                            <span className="text-sm font-medium text-foreground">
                              {event.time}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              cały dzień
                            </span>
                          )}
                        </div>
                        <div
                          className="w-1 h-8 rounded-full shrink-0"
                          style={{
                            backgroundColor: event.type === "task"
                              ? PRIORITY_COLORS[event.priority || "normal"]
                              : "#14B8A6",
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-foreground">
                            {event.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event.type === "task" ? "Zadanie" : "Aktywność"}
                            {event.user && ` · ${event.user.first_name} ${event.user.last_name}`}
                            {event.end_time && ` · do ${event.end_time}`}
                          </p>
                        </div>
                      </button>
                    </div>
                  )
                })
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
